<template>
  <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50" @click.self="$emit('close')">
    <div class="bg-white rounded-md shadow-lg p-5 w-[32rem] max-h-[80vh] overflow-y-auto">
      <h3 class="text-base font-semibold mb-3">
        {{ modalTitle }}
      </h3>

      <div class="text-xs text-gray-600 mb-3">
        {{ recordIds.length }} record<span v-if="recordIds.length !== 1">s</span> selected, total ${{ formatAmount(totalAmount) }}.
      </div>

      <label class="block text-sm mb-1">
        {{ referenceLabel }}
      </label>
      <input
        v-model="reference"
        type="text"
        class="w-full p-2 border border-gray-400 rounded-md mb-3"
        :placeholder="referencePlaceholder"
      />

      <label class="block text-sm mb-1">
        {{ effectiveDateLabel }}
      </label>
      <input
        v-model="effectiveDate"
        type="date"
        class="w-full p-2 border border-gray-400 rounded-md mb-3"
      />

      <div v-if="batchType === 'client_invoice'" class="mb-3">
        <label class="block text-sm mb-1">Client (optional — auto-derived from records)</label>
        <input
          :value="autoClientLabel"
          disabled
          class="w-full p-2 border border-gray-200 bg-gray-50 rounded-md text-gray-500 text-sm"
        />
        <div v-if="mixedClients" class="mt-1 text-xs text-red-600">
          Warning: selected records span multiple clients. Typical practice is one batch per client.
        </div>
      </div>

      <label class="block text-sm mb-1">Notes (optional)</label>
      <textarea
        v-model="notes"
        rows="2"
        class="w-full p-2 border border-gray-400 rounded-md mb-3"
      ></textarea>

      <div v-if="errorSummary" class="mb-3 p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700 whitespace-pre-wrap">
        {{ errorSummary }}
      </div>

      <div class="flex justify-end gap-2">
        <button
          class="text-sm px-3 py-1.5 border border-gray-400 rounded hover:bg-gray-100"
          @click="$emit('close')"
        >Cancel</button>
        <button
          class="text-sm px-3 py-1.5 rounded bg-accent-primary text-white hover:bg-accent-secondary disabled:opacity-50"
          :disabled="!canSubmit || applying"
          @click="apply"
        >{{ applying ? 'Applying...' : 'Apply' }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default { name: "bulk-batch-modal" };
</script>

<script setup>
import { ref, computed, defineProps, defineEmits } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import {
  bulkMarkReimbursed,
  bulkMarkClientInvoiced,
  bulkMarkBankReconciled,
} from "@/services/Expense.js";

const props = defineProps({
  batchType: { type: String, required: true },
  recordIds: { type: Array, required: true },
  records: { type: Array, required: true },
});
const emit = defineEmits(["close", "applied"]);

const store = useStore();
const $toast = useToast();

const reference = ref("");
const effectiveDate = ref(todayISO());
const notes = ref("");
const applying = ref(false);
const errorSummary = ref("");

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const modalTitle = computed(() => {
  if (props.batchType === 'payroll') return 'Mark as Reimbursed (Payroll)';
  if (props.batchType === 'client_invoice') return 'Mark as Client Invoiced';
  return 'Mark as Bank Reconciled';
});

const referenceLabel = computed(() => {
  if (props.batchType === 'payroll') return 'Payroll Run Reference';
  if (props.batchType === 'client_invoice') return 'Invoice Number';
  return 'Bank Statement Reference';
});

const referencePlaceholder = computed(() => {
  if (props.batchType === 'payroll') return 'e.g. PR-2026-03-20';
  if (props.batchType === 'client_invoice') return 'e.g. INV-2026-0412';
  return 'e.g. AMEX-2026-03';
});

const effectiveDateLabel = computed(() => {
  if (props.batchType === 'payroll') return 'Paid Date';
  if (props.batchType === 'client_invoice') return 'Invoice Date';
  return 'Bank Statement Date';
});

function formatAmount(n) {
  if (n === null || n === undefined) return "0.00";
  const num = typeof n === "string" ? parseFloat(n) : n;
  return num.toFixed(2);
}

const totalAmount = computed(() =>
  props.records.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0)
);

const clientIds = computed(() =>
  [...new Set(props.records.filter((r) => r.is_client_reimbursable).map((r) => r.client && r.client.id).filter(Boolean))]
);

const mixedClients = computed(() => props.batchType === 'client_invoice' && clientIds.value.length > 1);

const autoClientLabel = computed(() => {
  if (clientIds.value.length === 0) return '—';
  if (clientIds.value.length === 1) {
    const r = props.records.find((x) => x.is_client_reimbursable);
    return r && r.client_name ? r.client_name : `Client #${clientIds.value[0]}`;
  }
  return `Multiple (${clientIds.value.length})`;
});

const canSubmit = computed(() =>
  reference.value.trim() && effectiveDate.value && props.recordIds.length > 0
);

async function apply() {
  applying.value = true;
  errorSummary.value = "";
  const token = store.state.auth.token;
  const payload = {
    record_ids: props.recordIds,
    batch: {
      reference: reference.value.trim(),
      effective_date: effectiveDate.value,
      notes: notes.value || null,
    },
  };
  if (props.batchType === 'client_invoice' && clientIds.value.length === 1) {
    payload.batch.client = clientIds.value[0];
  }
  try {
    let result;
    if (props.batchType === 'payroll') {
      result = await bulkMarkReimbursed(token, payload);
    } else if (props.batchType === 'client_invoice') {
      result = await bulkMarkClientInvoiced(token, payload);
    } else {
      result = await bulkMarkBankReconciled(token, payload);
    }
    if (result.errors && result.errors.length > 0) {
      errorSummary.value = `Applied ${result.applied_ids.length}, failed ${result.errors.length}:\n`
        + result.errors.map((e) => `#${e.id}: ${e.detail}`).join('\n');
    } else {
      $toast.open({ message: `Applied to ${result.applied_ids.length} records`, type: 'success', duration: 2000 });
      emit("applied", result);
    }
  } catch (error) {
    const detail = error.response && error.response.data && JSON.stringify(error.response.data);
    errorSummary.value = detail || (error.message || 'Failed');
  }
  applying.value = false;
}
</script>
